import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import fetchWrapper from '@/services/fetchWrapper.service';
import AddArtworkForm from '@/shared-components/forms/add-artwork';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';

export default function AddArtwork() {
  const { state } = useStateHandler();
  const session = state?.user_session;
  const userType = session?.user_type;
  const isArtistAccount = userType === 'artist' || userType === 'gallery_artist';
  const navigate = useNavigate();
  const location = useLocation();
  const [permissionChecked, setPermissionChecked] = useState(!isArtistAccount);
  const [canAddArtworks, setCanAddArtworks] = useState(!isArtistAccount);
  const [showDeniedPopup, setShowDeniedPopup] = useState(false);

  const artistField = session?.gallery_artist;
  const artistId = typeof artistField === 'string' ? artistField : artistField?._id;
  const galleryId = (typeof artistField?.gallery === 'string'
    ? artistField.gallery
    : artistField?.gallery?._id) || session?.gallery_id;

  useEffect(() => {
    if (!isArtistAccount) {
      return;
    }

    if (!artistId || !galleryId) {
      setCanAddArtworks(false);
      setPermissionChecked(true);
      setShowDeniedPopup(true);
      return;
    }

    let isActive = true;

    (async () => {
      try {
        const { response, data } = await fetchWrapper.get(
          `${config.apis.api.url}/permissions/check?artist=${artistId}&gallery=${galleryId}`,
          { injectToken: true }
        );
        if (!isActive) return;
        if (!response.ok) {
          setCanAddArtworks(false);
          setPermissionChecked(true);
          setShowDeniedPopup(true);
          return;
        }

        const canAdd = Boolean(data?.permissions?.permissions?.addArtworks);
        setCanAddArtworks(canAdd);
        setPermissionChecked(true);
        setShowDeniedPopup(!canAdd);
      } catch (error) {
        if (!isActive) return;
        setCanAddArtworks(false);
        setPermissionChecked(true);
        setShowDeniedPopup(true);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [artistId, galleryId, isArtistAccount]);

  if (!permissionChecked) {
    return null;
  }

  return (
    <>
      {canAddArtworks ? (
        <AddArtworkForm 
          artworkData={location.state?.draftData} 
          changesRequired={location.state?.draftData?.draft_changes}
        />
      ) : (
        <ActionRejectedPopup
          open={showDeniedPopup}
          close={() => {
            setShowDeniedPopup(false);
            navigate('/profile', { replace: true });
          }}
          description="You do not have permission to upload artworks. Please contact your gallery admin."
        />
      )}
    </>
  );
}
